import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    Stack,
    Tooltip,
} from "@mui/material";
import React, {useEffect, useMemo, useState} from "react";
import Typography from "@mui/material/Typography";
import {useDocumentTitle} from "../../Hooks/useDocumentTitle";
import {ContractSearchHit, EUserGroup, GetSupportContractData} from "../../apiClient/Api";
import {useApplicationContext} from "../../ApplicationContext";
import dayjs from "dayjs";
import {AllInclusive, CalendarToday, Refresh, Speed} from "@mui/icons-material";
import {useNavigate} from "react-router";
import {Buffer} from 'buffer';
import {useTranslation} from "react-i18next";
import {useSnackbar} from "notistack";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faFileCertificate,
    faFileUpload,
    faLifeRing,
    faMobile,
} from '@fortawesome/pro-solid-svg-icons';
import {useSemanticSearch} from "../../Hooks/useSemanticSearch";
import {SemanticSearchBar} from "../../Components/SemanticSearchBar";
import {SearchScoreChip} from "../../Components/SearchScoreChip";

type StatusColor = 'success' | 'warning' | 'error' | 'info' | 'default';

/**
 * Maps the numeric contract status to a Material UI color + translated label.
 * Status values used in the backend: 0 = expired, 1 = expiring soon, 2 = active, 3 = special.
 */
const useContractStatus = () => {
    const {t} = useTranslation();
    return (status: number | undefined, infinite: boolean | undefined): { color: StatusColor; label: string } => {
        if (infinite) return {color: 'info', label: t('common.contractStatusUnlimited')};
        switch (status) {
            case 2:
                return {color: 'success', label: t('common.contractStatusActive')};
            case 1:
                return {color: 'warning', label: t('common.contractStatusExpiring')};
            case 0:
                return {color: 'error', label: t('common.contractStatusExpired')};
            case 3:
                return {color: 'info', label: t('common.contractStatusUnlimited')};
            default:
                return {color: 'default', label: '-'};
        }
    };
};

/**
 * Maps status color names to theme palette paths for the left border accent.
 */
const statusAccentColor: Record<StatusColor, string> = {
    success: 'success.main',
    warning: 'warning.main',
    error: 'error.main',
    info: 'info.main',
    default: 'grey.400',
};

/**
 * SupportContracts component displays all support contracts for the current user
 * as a responsive grid of compact cards with consistent height and a status accent.
 */
export const SupportContracts = () => {
    useDocumentTitle("base.pages.supportContracts");
    const [data, setData] = useState<GetSupportContractData[]>([]);
    const [isFetching, setIsFetching] = useState(false);
    const [isData, setIsData] = useState(false);
    const [recalculatingId, setRecalculatingId] = useState<string | null>(null);
    const [recalculatingAll, setRecalculatingAll] = useState(false);
    const context = useApplicationContext();
    const navigate = useNavigate();
    const {t} = useTranslation();
    const {enqueueSnackbar} = useSnackbar();
    const getStatus = useContractStatus();

    const isAdmin = (context.userData?.userGroup ?? EUserGroup.CUSTOMER) <= EUserGroup.ADMIN;
    const isInternal = (context.userData?.userGroup ?? EUserGroup.CUSTOMER) <= EUserGroup.OFFICE;

    const search = useSemanticSearch<ContractSearchHit>({
        searchFn: async (query) => (await context.api.search.searchSearchContracts({query, limit: 100})).data,
        extractKey: (hit) => ({key: hit.contractId, score: hit.score}),
    });

    const displayedData = useMemo(() => {
        if (!search.hits) return data;
        const hits = search.hits;
        return data
            .filter(c => c.id && hits.has(c.id))
            .sort((a, b) => (hits.get(b.id!) ?? 0) - (hits.get(a.id!) ?? 0));
    }, [data, search.hits]);

    /**
     * Fetches support contracts from API
     */
    async function fetchData() {
        setIsFetching(true);
        const response = await context.api.supportContract.supportContractListSupportContracts();
        if (response && response.data) setData(response.data);
        setIsFetching(false);
    }

    useEffect(() => {
        if (!isData) {
            setIsData(true);
            fetchData();
        }
    }, [data, isData, isFetching]);

    /**
     * Recalculates and persists the service-units snapshot for a single contract (preview → confirm).
     */
    const recalculateContract = async (id: string) => {
        setRecalculatingId(id);
        try {
            const preview = await context.api.supportContract.supportContractRecalculateContractServiceUnits(id);
            if (preview.data.token) {
                await context.api.supportContract.supportContractConfirmContractServiceUnits(id, {token: preview.data.token});
            }
            enqueueSnackbar(t('common.serviceUnitsRecalculateSuccess'), {variant: "success"});
            await fetchData();
        } catch (e) {
            context.apiError(e);
        } finally {
            setRecalculatingId(null);
        }
    };

    /**
     * Admin: recalculates snapshots for all licenses and contracts in one shot (no token).
     */
    const recalculateAll = async () => {
        setRecalculatingAll(true);
        try {
            const result = await context.api.api.licenseRecalculateAllServiceUnits();
            enqueueSnackbar(t('common.serviceUnitsRecalculateAllSuccess', {
                licenses: result.data.licensesUpdated ?? 0,
                contracts: result.data.contractsUpdated ?? 0,
            }), {variant: "success"});
            await fetchData();
        } catch (e) {
            context.apiError(e);
        } finally {
            setRecalculatingAll(false);
        }
    };

    /**
     * Requests contract renewal for specified duration
     */
    const renewContract = (id: string, duration: number) => () => {
        context.api.supportContract.supportContractRemewContract({contract: id, duration}).then(() => {
            alert("Vielen dank für Ihre Anfrage. Sie erhalten in wenigen Tagen Ihr Angebot");
        }).catch(context.apiError);
    };

    return (
        <Box sx={{width: '100%'}}>
            <Box sx={{
                display: 'flex',
                flexDirection: {xs: 'column', sm: 'row'},
                alignItems: {xs: 'stretch', sm: 'flex-start'},
                gap: 2,
                mb: 2,
                px: 1,
            }}>
                <Box sx={{flex: 1, maxWidth: {sm: 480}}}>
                    <SemanticSearchBar
                        value={search.query}
                        onChange={search.setQuery}
                        loading={search.loading}
                        unavailable={search.unavailable}
                        error={search.error}
                        matchCount={search.hits ? displayedData.length : undefined}
                        placeholder={t('common.searchContractsPlaceholder')}
                    />
                </Box>
                {isAdmin && (
                    <Box sx={{display: 'flex', justifyContent: 'flex-end', flexShrink: 0}}>
                        <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            startIcon={recalculatingAll ? <CircularProgress size={16}/> : <Refresh/>}
                            disabled={recalculatingAll}
                            onClick={recalculateAll}
                        >
                            {t('common.serviceUnitsRecalculateAll')}
                        </Button>
                    </Box>
                )}
            </Box>

            <Grid container spacing={2} sx={{px: 1, alignItems: 'stretch'}}>
                {displayedData.map(elem => {
                    const status = getStatus(elem.status, elem.infinite);
                    const showRenewalActions = !isInternal && (elem.status ?? 0) < 2 && !elem.infinite;
                    const showUpdatesSection = isInternal;
                    const matchScore = elem.id ? search.hits?.get(elem.id) : undefined;

                    return (
                        <Grid key={elem.id} size={{xs: 12, sm: 6, md: 4, lg: 3}} sx={{display: 'flex'}}>
                            <Card
                                variant="outlined"
                                sx={{
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderLeft: 4,
                                    borderLeftColor: statusAccentColor[status.color],
                                    transition: 'box-shadow 120ms ease-in-out',
                                    '&:hover': {boxShadow: 3},
                                }}
                            >
                                <CardContent sx={{display: 'flex', flexDirection: 'column', gap: 1.25, pb: 1}}>
                                    {/* Header: Vertragsnummer + Status */}
                                    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1}}>
                                        <Box sx={{minWidth: 0, flex: 1}}>
                                            <Typography
                                                variant="h6"
                                                color="primary"
                                                sx={{
                                                    lineHeight: 1.2,
                                                    fontFamily: 'monospace',
                                                    fontWeight: 600,
                                                    wordBreak: 'break-all',
                                                }}
                                            >
                                                {elem.contractId}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                noWrap
                                                title={elem.customerName ?? ''}
                                            >
                                                {elem.customerName}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{fontWeight: 500}}
                                                noWrap
                                                title={elem.title ?? ''}
                                            >
                                                {elem.title}
                                            </Typography>
                                        </Box>
                                        <Stack direction="column" spacing={0.5} sx={{alignItems: 'flex-end', flexShrink: 0}}>
                                            <Chip
                                                size="small"
                                                label={status.label}
                                                color={status.color === 'default' ? 'default' : status.color}
                                                sx={{fontWeight: 600}}
                                            />
                                            {matchScore != null && <SearchScoreChip score={matchScore}/>}
                                        </Stack>
                                    </Box>

                                    {/* Metadata-Chips */}
                                    <Stack direction="row" spacing={1} sx={{flexWrap: 'wrap', rowGap: 0.75}}>
                                        {elem.infinite ? (
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                icon={<AllInclusive/>}
                                                label={t('common.contractStatusUnlimited')}
                                            />
                                        ) : elem.expire && (
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                icon={<CalendarToday/>}
                                                label={dayjs(elem.expire.toString()).format("DD.MM.YYYY")}
                                                title={t('common.validUntil')}
                                            />
                                        )}

                                        {elem.serviceEinheitenTotalSnapshot != null && (
                                            <Tooltip
                                                title={elem.serviceEinheitenSnapshotStale
                                                    ? t('common.serviceUnitsStale')
                                                    : t('common.serviceUnits')}
                                            >
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    icon={<Speed/>}
                                                    label={`${elem.serviceEinheitenTotalSnapshot.toLocaleString(undefined, {
                                                        minimumFractionDigits: 0,
                                                        maximumFractionDigits: 2,
                                                    })}${elem.serviceEinheitenSnapshotStale ? '*' : ''} SE`}
                                                    sx={elem.serviceEinheitenSnapshotStale ? {fontStyle: 'italic', opacity: 0.8} : undefined}
                                                    onDelete={isInternal && elem.id ? () => recalculateContract(elem.id!) : undefined}
                                                    deleteIcon={
                                                        recalculatingId === elem.id
                                                            ? <CircularProgress size={14}/>
                                                            : <Refresh fontSize="small"/>
                                                    }
                                                />
                                            </Tooltip>
                                        )}
                                    </Stack>

                                    {((elem.supportCodes?.length ?? 0) > 0
                                        || (showUpdatesSection && (elem.filterTags?.length ?? 0) >= 0)) && (
                                        <Divider sx={{my: 0.25}}/>
                                    )}

                                    {/* Anlagen */}
                                    {(elem.supportCodes?.length ?? 0) > 0 && (
                                        <Box>
                                            <Typography
                                                variant="overline"
                                                color="text.secondary"
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: '0.65rem',
                                                    letterSpacing: 0.5,
                                                    lineHeight: 1.5,
                                                }}
                                            >
                                                {t('common.depositedAttachments')} ({elem.supportCodes!.length})
                                            </Typography>
                                            <Stack spacing={0.5} sx={{mt: 0.5}}>
                                                {elem.supportCodes!.map(data => (
                                                    <Box
                                                        key={data.id}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                            px: 1,
                                                            py: 0.5,
                                                            borderRadius: 1,
                                                            bgcolor: 'action.hover',
                                                        }}
                                                    >
                                                        <Box sx={{minWidth: 0, flex: 1}}>
                                                            <Typography
                                                                variant="body2"
                                                                noWrap
                                                                sx={{fontWeight: 500}}
                                                                title={data.name ?? ''}
                                                            >
                                                                {data.name}
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{fontFamily: 'monospace', display: 'block'}}
                                                                noWrap
                                                            >
                                                                {data.id}
                                                            </Typography>
                                                        </Box>
                                                        <Stack direction="row" sx={{flexShrink: 0}}>
                                                            <Tooltip title={`${t('common.supportRequestFor')} ${data.name ?? ''}`}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => navigate(`/support?si=${Buffer.from(data.id!).toString('base64')}&qs=true`)}
                                                                >
                                                                    <FontAwesomeIcon icon={faLifeRing} size="sm"/>
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title={t('common.manageDevices')}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => navigate(`/deviceManagement/${data.id}`)}
                                                                >
                                                                    <FontAwesomeIcon icon={faMobile} size="sm"/>
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title={t('common.manageLicenses')}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => navigate(`/licenses/${data.id}`)}
                                                                >
                                                                    <FontAwesomeIcon icon={faFileCertificate} size="sm"/>
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Stack>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Box>
                                    )}

                                    {/* Updates (internal only) */}
                                    {showUpdatesSection && (
                                        <Box>
                                            <Typography
                                                variant="overline"
                                                color="text.secondary"
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: '0.65rem',
                                                    letterSpacing: 0.5,
                                                    lineHeight: 1.5,
                                                }}
                                            >
                                                {t('common.availableComponentUpdates')}
                                            </Typography>
                                            {(elem.filterTags?.length ?? 0) === 0 ? (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{fontStyle: 'italic', display: 'block', mt: 0.5}}
                                                >
                                                    {t('common.noUpdatesAvailable')}
                                                </Typography>
                                            ) : (
                                                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5}}>
                                                    {elem.filterTags!.map(tag => (
                                                        <Chip
                                                            key={tag.name}
                                                            label={tag.name}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    ))}
                                                </Box>
                                            )}
                                        </Box>
                                    )}

                                    {/* Spacer to push action footer to the bottom */}
                                    <Box sx={{flex: 1}}/>
                                </CardContent>

                                <CardActions sx={{flexDirection: 'column', alignItems: 'stretch', gap: 0.5, px: 2, pb: 2, pt: 0}}>
                                    <Button
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<FontAwesomeIcon icon={faFileUpload}/>}
                                        onClick={() => navigate(`/fileshare/${elem.id}`)}
                                    >
                                        {t('common.fileSharing')}
                                    </Button>

                                    {showRenewalActions && (
                                        <Stack direction="row" spacing={0.5} sx={{mt: 0.5}}>
                                            <Button
                                                fullWidth
                                                size="small"
                                                variant="outlined"
                                                color="warning"
                                                onClick={renewContract(elem.contractId!, 1)}
                                            >
                                                1 {t('common.month')}
                                            </Button>
                                            <Button
                                                fullWidth
                                                size="small"
                                                variant="outlined"
                                                color="primary"
                                                onClick={renewContract(elem.contractId!, 12)}
                                            >
                                                12 {t('common.month')}
                                            </Button>
                                            <Button
                                                fullWidth
                                                size="small"
                                                variant="outlined"
                                                color="success"
                                                onClick={renewContract(elem.contractId!, 24)}
                                            >
                                                24 {t('common.month')}
                                            </Button>
                                        </Stack>
                                    )}
                                </CardActions>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );
};
